import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getSessionSurvey,
  type SessionSurvey,
  type SurveyComplexity,
  type SurveyGuidance,
  type SurveyPerformance, getMySessionSurvey } from '../api/sessionMetrics';

/**
 * The participant's own rating of a recording (CAPTURE-SESSION-SURVEY), asked
 * on the capture-app at the end of the case.
 *
 * Three states worth telling apart, which is why this renders something in all
 * of them rather than hiding when empty:
 *   - answered  → the three scale values;
 *   - declined  → they were asked and said no (a fact about consent, not a
 *     gap in the data collection);
 *   - not asked → nothing stored: an older capture-app build, or a recording
 *     from before the survey existed.
 *
 * Labels here mirror the capture-app's, but the stored values are the stable
 * tokens — read those, never these.
 */

const GUIDANCE_LABEL: Record<SurveyGuidance, string> = {
  show_and_tell: 'Show & Tell',
  active_help: 'Active Help',
  passive_help: 'Passive Help',
  supervision_only: 'Supervision Only',
};

const PERFORMANCE_LABEL: Record<SurveyPerformance, string> = {
  unprepared: 'Unprepared / Critical Deficiency',
  inexperienced: 'Inexperienced w/ Procedure',
  intermediate: 'Intermediate Performance',
  practice_ready: 'Practice-Ready Performance',
  exceptional: 'Exceptional Performance',
};

const COMPLEXITY_LABEL: Record<SurveyComplexity, string> = {
  easiest_third: 'Easiest 1/3',
  average: 'Average',
  hardest_third: 'Hardest 1/3',
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="baseline"
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function SessionSurveyCard({
  tenantId,
  sessionId,
}: {
  tenantId: string | null | undefined;
  sessionId: string | null;
}) {
  const [survey, setSurvey] = useState<SessionSurvey | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    setSurvey(null);
    if (!sessionId) {
      setLoaded(true);
      return;
    }
    // With a tenant id this is an admin looking at a participant's session;
    // without one it is the participant's own dashboard, which asks by
    // session alone. Until the second path existed the card could not fetch
    // there and reported every session as "Not asked" (2026-09-21).
    (tenantId ? getSessionSurvey(tenantId, sessionId) : getMySessionSurvey(sessionId))
      .then((s) => {
        if (alive) setSurvey(s);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [tenantId, sessionId]);

  if (!loaded || !sessionId) return null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.default',
        px: 1,
        py: 0.75,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
        >
          SELF-ASSESSMENT
        </Typography>
        {survey?.declined && (
          <Chip size="small" variant="outlined" label="declined" sx={{ height: 18, fontSize: 11 }} />
        )}
      </Stack>

      {survey === null ? (
        <Typography variant="caption" color="text.secondary">
          Not asked — this recording predates the end-of-session survey, or the
          device is on an older app version.
        </Typography>
      ) : survey.declined ? (
        <Typography variant="caption" color="text.secondary">
          The participant was offered the survey and chose not to answer.
        </Typography>
      ) : (
        <Stack spacing={0.25}>
          <Row
            label="Guidance received"
            value={
              survey.guidance ? GUIDANCE_LABEL[survey.guidance] : '—'
            }
          />
          <Row
            label="Own performance"
            value={
              survey.performance
                ? PERFORMANCE_LABEL[survey.performance]
                : '—'
            }
          />
          <Row
            label="Case complexity"
            value={
              survey.complexity ? COMPLEXITY_LABEL[survey.complexity] : '—'
            }
          />
        </Stack>
      )}
    </Box>
  );
}
